import React, { useState } from "react";
import * as XLSX from "xlsx";
import { collection, writeBatch, doc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../../firebase";
import { showSuccessToast, showErrorToast } from "../../toastmsg";
import { transformKeysToCamelCase } from "../../utils/case";

interface UploadExcelProps<T extends Record<string, any>> {
  collectionName: string;

  /**
   * Transform a row from Excel into the Firestore document shape.
   * Return null/undefined to skip the row.
   */
  transformRow: (row: Record<string, any>) => T | null | undefined;

  acceptedTypes?: string;
  buttonLabel?: string;

  onUploadComplete?: (result: {
    added: number;
    pendingReview?: number;
    protectedMatches?: number;
    errors: { row: any; error: string }[];
  }) => void;
}

function UploadLeads<T extends Record<string, any> = any>({
  collectionName,
  transformRow,
  acceptedTypes = ".xlsx,.xls",
  buttonLabel = "Upload Excel",
  onUploadComplete,
}: UploadExcelProps<T>) {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<{
    added: number;
    pendingReview?: number;
    protectedMatches?: number;
    errors: { row: any; error: string }[];
  } | null>(null);

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setResult(null);

    try {
      // ✅ 1. Parse Excel
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array", cellDates: true });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rawJson = XLSX.utils.sheet_to_json<Record<string, any>>(worksheet, {
        raw: false,
      });

      // ✅ 2. Convert headers to camelCase
      const camelHeaderJson = rawJson.map((row) => ({
        ...row,
        ...transformKeysToCamelCase(row),
      }));

      // ✅ 3. Transform rows (business logic only)
      const transformed = camelHeaderJson
        .map(transformRow)
        .filter((row): row is T => !!row);

      if (transformed.length === 0) {
        showErrorToast("No valid rows found in Excel file.");
        return;
      }

      // ✅ 4. Check duplicates via Cloud Function
      // Function is deployed in asia-southeast1
      const functions = getFunctions(undefined, "asia-southeast1");

      if (collectionName === "leads") {
        const bulkUpsertStaffLeads = httpsCallable(
          functions,
          "bulkUpsertStaffLeads"
        );
        const upsertResult = await bulkUpsertStaffLeads({ rows: transformed });
        const data = upsertResult.data as any;
        const rowResults = Array.isArray(data.results) ? data.results : [];
        const errors = rowResults
          .filter((row: any) => !row.success)
          .map((row: any) => ({
            row: row.row,
            error: row.error || "Unknown upload error",
          }));
        const added = data.created || 0;
        const pendingReview = data.pendingReview || 0;
        const protectedMatches = data.protectedMatches || 0;
        const result = { added, pendingReview, protectedMatches, errors };

        setResult(result);
        showSuccessToast(
          `Upload complete: ${added} created, ${pendingReview} pending review (${protectedMatches} protected authenticated matches), ${errors.length} errors.`
        );

        if (onUploadComplete) {
          onUploadComplete(result);
        }
        return;
      }

      const processedRows: T[] = transformed;

      // ✅ 5. Batch upload
      const batch = writeBatch(db);
      let added = 0;
      const errors: { row: any; error: string }[] = [];

      for (const row of processedRows) {
        try {
          const docRef = doc(collection(db, collectionName));

          batch.set(docRef, {
            ...row,
            status: row.status || "New",
            uploadedAt: new Date().toISOString(),
          });

          added++;
        } catch (err: any) {
          errors.push({ row, error: err?.message || String(err) });
        }
      }

      // ✅ 6. Commit batch
      try {
        await batch.commit();
      } catch (err: any) {
        errors.push(
          ...processedRows.map((row) => ({
            row,
            error: err?.message || String(err),
          }))
        );
        added = 0;
      }

      const result = { added, errors };
      setResult(result);

      showSuccessToast(
        `Upload complete: ${added} uploaded, ${errors.length} errors.`
      );

      if (onUploadComplete) {
        onUploadComplete(result);
      }
    } catch (err: any) {
      showErrorToast("Upload failed: " + (err?.message || String(err)));
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  return (
    <label className="relative cursor-pointer inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md font-medium shadow hover:bg-blue-700 transition-colors duration-150 w-fit">
      <span>{uploading ? "Uploading..." : buttonLabel}</span>
      <input
        type="file"
        accept={acceptedTypes}
        onChange={handleFileUpload}
        disabled={uploading}
        className="absolute inset-0 opacity-0 cursor-pointer"
      />
    </label>
  );
}

export default UploadLeads;
